import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Flag from "react-world-flags";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Eye, Users, FileText, Flag as FlagIcon, TrendingUp, Loader2 } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-[0.15em] font-semibold">{label}</span>
      </div>
      <p className="font-serif text-3xl font-bold">{value}</p>
    </div>
  );
}

export default function AdminStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (stats === false) return <p className="text-sm text-muted-foreground">Statistiche non disponibili.</p>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={FileText} label="Pubblicati" value={stats.published} testid="stat-published" />
        <StatCard icon={Eye} label="Visualizzazioni" value={stats.total_views} testid="stat-views" />
        <StatCard icon={Users} label="Iscritti" value={stats.subscribers} testid="stat-subscribers" />
        <StatCard icon={FileText} label="Bozze" value={stats.drafts} testid="stat-drafts" />
        <StatCard icon={FlagIcon} label="Segnalazioni" value={stats.open_reports} testid="stat-reports" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Newsletter growth */}
        <div className="bg-card border border-border rounded-sm p-5" data-testid="newsletter-growth-chart">
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-4">
            <TrendingUp className="h-4 w-4 text-primary" /> Crescita iscritti (30 giorni)
          </h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.newsletter_growth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={6} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(v, n) => [v, n === "totale" ? "Totale iscritti" : "Nuovi"]}
                />
                <Area type="monotone" dataKey="totale" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top viewed */}
        <div className="bg-card border border-border rounded-sm p-5" data-testid="top-speeches">
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-4">
            <Eye className="h-4 w-4 text-primary" /> Discorsi più visti
          </h3>
          {stats.top_speeches.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun dato ancora.</p>
          ) : (
            <div className="space-y-1">
              {stats.top_speeches.map((s, i) => (
                <Link
                  key={s.id}
                  to={`/discorso/${s.id}`}
                  target="_blank"
                  data-testid={`top-speech-${s.id}`}
                  className="flex items-center gap-3 p-2 rounded-sm hover:bg-muted transition-colors"
                >
                  <span className="font-mono text-sm text-muted-foreground w-5">{i + 1}</span>
                  {s.country_code && (
                    <span className="w-6 h-4 overflow-hidden rounded-[2px] border border-border shrink-0">
                      <Flag code={s.country_code} className="w-full h-full object-cover" fallback={<span />} />
                    </span>
                  )}
                  <span className="flex-1 min-w-0 truncate text-sm">{s.title}</span>
                  <span className="text-xs font-mono text-muted-foreground inline-flex items-center gap-1 shrink-0">
                    <Eye className="h-3 w-3" /> {s.views || 0}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
